import { PDFDocument, StandardFonts } from "pdf-lib"

import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import {
  ALTO,
  ANCHO,
  escribible,
  GRIS,
  LINEA,
  type Lienzo,
  lineas,
  MARGEN,
  membrete,
  nuevaPagina,
  ROJO,
  texto,
  UTIL,
} from "./pdfReporte"
import { type Gastos, nombreDelMes } from "./tiposGastos"

/**
 * El PDF del reporte semanal de gastos administrativos, compuesto en el navegador
 * (13/10). El papel y el membrete del reporte de cuentas por pagar (`pdfReporte.ts`):
 * el resumen arriba, y tres tablas —pagos de la semana, presupuesto por partida y gasto
 * del mes por categoría—.
 */

type Columna = { rotulo: string; ancho: number; derecha?: boolean }

const usd = (t: string) => `USD ${monto(t)}`

/** Una tabla con cabecera que se repite al saltar de página. */
function tabla(l: Lienzo, titulo: string, columnas: Columna[], filas: string[][]) {
  const libre = UTIL - columnas.reduce((s, c) => s + c.ancho, 0) - (columnas.length - 1) * 6
  const anchos = columnas.map((c) => (c.ancho === 0 ? libre : c.ancho))
  const cabecera = (sigue: boolean) => {
    texto(l, `${titulo}${sigue ? " (continúa)" : ""}`, MARGEN, l.y, {
      cuerpo: 10.5,
      fuente: l.negrita,
    })
    l.y -= 16
    let x = MARGEN
    columnas.forEach((c, i) => {
      texto(l, c.rotulo, x, l.y, {
        cuerpo: 7.5,
        fuente: l.negrita,
        color: GRIS,
        derecha: c.derecha ? anchos[i] : undefined,
      })
      x += (anchos[i] ?? 0) + 6
    })
    l.y -= 5
    l.pagina.drawLine({
      start: { x: MARGEN, y: l.y },
      end: { x: ANCHO - MARGEN, y: l.y },
      thickness: 0.5,
      color: LINEA,
    })
    l.y -= 11
  }
  if (l.y < MARGEN + 70) nuevaPagina(l)
  cabecera(false)
  for (const fila of filas) {
    const partidas = fila.map((celda, i) =>
      columnas[i]?.derecha ? [celda] : lineas(l.normal, 8.5, celda, anchos[i] ?? 0),
    )
    const alto = Math.max(...partidas.map((p) => p.length)) * 10.5 + 6
    if (l.y - alto < MARGEN + 14) {
      nuevaPagina(l)
      cabecera(true)
    }
    let x = MARGEN
    partidas.forEach((p, i) => {
      p.forEach((ln, k) => {
        texto(l, ln, x, l.y - k * 10.5, {
          derecha: columnas[i]?.derecha ? anchos[i] : undefined,
          color: ln.startsWith("USD -") ? ROJO : undefined,
        })
      })
      x += (anchos[i] ?? 0) + 6
    })
    l.y -= alto
    l.pagina.drawLine({
      start: { x: MARGEN, y: l.y + 8 },
      end: { x: ANCHO - MARGEN, y: l.y + 8 },
      thickness: 0.4,
      color: LINEA,
    })
  }
  l.y -= 18
}

export async function pdfDeLosGastos(
  g: Gastos,
  logo: Uint8Array | null,
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(g.titulo)
  const l: Lienzo = {
    pdf,
    pagina: pdf.addPage([ANCHO, ALTO]),
    y: ALTO - MARGEN,
    normal: await pdf.embedFont(StandardFonts.Helvetica),
    negrita: await pdf.embedFont(StandardFonts.HelveticaBold),
  }
  const { semana: s, mes: m, presupuesto: p } = g
  membrete(l, g, logo ? await pdf.embedPng(logo) : null)
  texto(l, g.titulo, MARGEN, l.y, { cuerpo: 16, fuente: l.negrita })
  l.y -= 15
  texto(l, `Generado el ${comoFechaHora(g.generado_en)} · montos en dólares`, MARGEN, l.y, {
    color: GRIS,
  })
  l.y -= 22
  const resumen = [
    `Pagado la semana pasada: ${usd(s.total)} en ${s.pagos.length} pago(s)`,
    `${nombreDelMes(m.anio, m.mes)} al ${comoFecha(m.hasta)}: ejecutado ${usd(m.ejecutado)} (fijos ${usd(m.fijos)} · variables ${usd(m.variables)})`,
    `Pendiente por ejecutar (aprobadas y en aprobación): ${usd(m.pendiente)} en ${m.ordenes_pendientes} orden(es)`,
    `Presupuesto: tope ${usd(p.tope)} · usado ${usd(p.usado)} · disponible ${usd(p.disponible)} · fuera de partida ${usd(p.fuera_de_partida)}`,
  ]
  for (const r of resumen) {
    texto(l, r, MARGEN, l.y, { cuerpo: 9.5, fuente: l.negrita })
    l.y -= 14
  }
  l.y -= 12

  tabla(
    l,
    `Pagos de la semana (${comoFecha(s.desde)} al ${comoFecha(s.hasta)})`,
    [
      { rotulo: "Fecha", ancho: 60 },
      { rotulo: "Orden", ancho: 80 },
      { rotulo: "Proveedor", ancho: 190 },
      { rotulo: "Concepto", ancho: 0 },
      { rotulo: "Monto", ancho: 90, derecha: true },
    ],
    s.pagos.length
      ? s.pagos.map((x) => [comoFecha(x.fecha), x.codigo, x.proveedor, x.concepto, usd(x.monto)])
      : [["—", "", "Sin pagos esa semana", "", ""]],
  )
  tabla(
    l,
    "Presupuesto del mes por partida",
    [
      { rotulo: "Partida", ancho: 0 },
      { rotulo: "Tope", ancho: 90, derecha: true },
      { rotulo: "Ejecutado", ancho: 90, derecha: true },
      { rotulo: "Comprometido", ancho: 90, derecha: true },
      { rotulo: "Disponible", ancho: 90, derecha: true },
      { rotulo: "Uso", ancho: 50, derecha: true },
    ],
    p.partidas.map((x) => [
      x.categoria,
      usd(x.tope),
      usd(x.ejecutado),
      usd(x.comprometido),
      usd(x.disponible),
      `${x.porcentaje} %`,
    ]),
  )
  tabla(
    l,
    `Gasto de ${nombreDelMes(m.anio, m.mes)} por categoría`,
    [
      { rotulo: "Categoría", ancho: 0 },
      { rotulo: "Tipo", ancho: 80 },
      { rotulo: "Órdenes", ancho: 60, derecha: true },
      { rotulo: "Total", ancho: 100, derecha: true },
    ],
    m.por_categoria.map((x) => [
      x.categoria,
      x.tipo === "FIJO" ? "Fijo" : x.tipo === "VARIABLE" ? "Variable" : "—",
      String(x.ordenes),
      usd(x.total),
    ]),
  )
  const sin = [...s.sin_convertir, ...m.sin_convertir]
  if (sin.length) {
    if (l.y < MARGEN + 40) nuevaPagina(l)
    texto(l, "Sin convertir a dólares", MARGEN, l.y, { cuerpo: 9.5, fuente: l.negrita })
    l.y -= 13
    for (const x of sin) {
      texto(l, `• ${x}`, MARGEN, l.y, { cuerpo: 8.5, color: GRIS })
      l.y -= 12
    }
  }

  const paginas = pdf.getPages()
  paginas.forEach((pag, i) => {
    const pie = escribible(
      l.normal,
      `${g.empresa_nombre} · ${g.titulo} · página ${i + 1} de ${paginas.length}`,
    )
    pag.drawText(pie, {
      x: ANCHO - MARGEN - l.normal.widthOfTextAtSize(pie, 7),
      y: MARGEN / 2,
      size: 7,
      font: l.normal,
      color: GRIS,
    })
  })
  return new Uint8Array(await pdf.save())
}
